import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';

interface AuditLog {
  id: string;
  actorId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  details: Record<string, unknown>;
  createdAt: string;
}

interface PaginatedAuditResponse {
  data: AuditLog[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export function AuditPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [filterEntity, setFilterEntity] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('per_page', '30');
      if (filterEntity) params.set('entity_type', filterEntity);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);

      const res = await apiFetch<PaginatedAuditResponse>(
        `/api/admin/dashboard/audit-logs?${params.toString()}`,
      );

      // The audit-logs endpoint returns a paginated format directly
      // where data, total, page, etc. are at the top level of the response
      const responseData = res as unknown as {
        success: boolean;
        data: AuditLog[];
        total: number;
        page: number;
        per_page: number;
        total_pages: number;
      };

      if (responseData.success) {
        setLogs(responseData.data || []);
        setTotalPages(responseData.total_pages || 1);
        setTotal(responseData.total || 0);
      } else {
        setError('Failed to load audit logs');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [page, filterEntity, filterDateFrom, filterDateTo]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  function applyFilters() {
    setPage(1);
    // loadLogs will re-run due to page change via useEffect
  }

  function clearFilters() {
    setFilterEntity('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setPage(1);
  }

  return (
    <div>
      <h1 style={styles.pageTitle}>Audit Logs</h1>
      <p style={styles.pageSubtitle}>{total} total entries</p>

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.filterField}>
          <label style={styles.label}>Entity Type</label>
          <select
            value={filterEntity}
            onChange={(e) => setFilterEntity(e.target.value)}
          >
            <option value="">All</option>
            <option value="user">User</option>
            <option value="pack">Pack</option>
            <option value="camp">Camp</option>
            <option value="cabin">Cabin</option>
            <option value="equipment">Equipment</option>
            <option value="trail">Trail</option>
            <option value="poi">POI</option>
            <option value="area">Area</option>
            <option value="almanac_config">Almanac Config</option>
            <option value="almanac_year">Almanac Year</option>
            <option value="system">System</option>
          </select>
        </div>
        <div style={styles.filterField}>
          <label style={styles.label}>From</label>
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
          />
        </div>
        <div style={styles.filterField}>
          <label style={styles.label}>To</label>
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
          />
        </div>
        <div style={styles.filterActions}>
          <button style={styles.btnSmall} onClick={applyFilters}>Apply</button>
          <button style={styles.btnSmallMuted} onClick={clearFilters}>Clear</button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : error ? (
        <div style={styles.error}>{error}</div>
      ) : logs.length === 0 ? (
        <p style={styles.empty}>No audit logs found</p>
      ) : (
        <>
          <div style={styles.card}>
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Entity</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {log.createdAt
                        ? new Date(log.createdAt).toLocaleString()
                        : '--'}
                    </td>
                    <td>
                      <div style={{ color: COLORS.textPrimary, fontSize: 13 }}>
                        {log.actorName || 'System'}
                      </div>
                      {log.actorEmail && (
                        <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                          {log.actorEmail}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge badge--info">{log.action}</span>
                    </td>
                    <td>
                      <span className="badge badge--muted">{log.entityType}</span>
                      {log.entityId && (
                        <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>
                          {log.entityId.slice(0, 8)}...
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: COLORS.textMuted, maxWidth: 300 }}>
                      {log.details ? truncateJson(log.details) : '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={styles.pagination}>
            <button
              style={styles.btnSmallMuted}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span style={styles.pageInfo}>
              Page {page} of {totalPages}
            </span>
            <button
              style={styles.btnSmallMuted}
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function truncateJson(obj: Record<string, unknown>): string {
  const str = JSON.stringify(obj);
  return str.length > 80 ? str.slice(0, 80) + '...' : str;
}

const styles: Record<string, React.CSSProperties> = {
  pageTitle: { fontSize: 28, fontWeight: 700, color: COLORS.textPrimary, margin: 0 },
  pageSubtitle: { fontSize: 14, color: COLORS.textMuted, marginTop: 4, marginBottom: 24 },
  filters: {
    display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 20,
    padding: 16, backgroundColor: COLORS.backgroundSecondary,
    border: `1px solid ${COLORS.borderDefault}`, borderRadius: 12,
    flexWrap: 'wrap',
  },
  filterField: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterActions: { display: 'flex', gap: 8, alignItems: 'flex-end', paddingBottom: 2 },
  card: {
    backgroundColor: COLORS.backgroundSecondary, border: `1px solid ${COLORS.borderDefault}`,
    borderRadius: 12, padding: 16,
  },
  pagination: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    gap: 16, marginTop: 20,
  },
  pageInfo: { fontSize: 13, color: COLORS.textMuted },
  empty: { color: COLORS.textMuted, fontSize: 13, fontStyle: 'italic' },
  loading: { display: 'flex', justifyContent: 'center', height: 200, color: COLORS.textMuted, alignItems: 'center' },
  error: { padding: 20, backgroundColor: 'rgba(220,53,69,0.1)', borderRadius: 8, color: COLORS.statusError },
  label: { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  btnSmall: {
    padding: '6px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.brassLight,
    border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 500, cursor: 'pointer',
  },
  btnSmallMuted: {
    padding: '6px 14px', backgroundColor: 'transparent', color: COLORS.textMuted,
    border: `1px solid ${COLORS.borderDefault}`, borderRadius: 4, fontSize: 12, cursor: 'pointer',
  },
};
